import * as React from "react";
import { useState, useRef, useCallback } from "react";
import { Loader2, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import {
  useAddressSearch,
  formatAdsAddress,
  type AdsAddressResult,
} from "@/hooks/useAddressSearch";
import { sanitizeString } from "@/utils/sanitization";

export interface AddressAutocompleteProps {
  /** Current address string value */
  value: string;
  /** Called when user types in the input (address text changes) */
  onChange: (address: string) => void;
  sanitizeInput?: (address: string) => string;
  /** Called when user selects an address from the dropdown */
  onSelect?: (result: AdsAddressResult) => void;
  /** Called when the input loses focus (for auto-save / persist patterns) */
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

/**
 * AddressAutocomplete renders as a single <Input> element at its root,
 * making it compatible with FormControl's Slot-based id/aria injection.
 * The Popover dropdown is anchored to the input via PopoverAnchor (no
 * PopoverTrigger wrapper), so FormControl's id flows directly to <Input>.
 */
export const AddressAutocomplete = React.forwardRef<HTMLInputElement, AddressAutocompleteProps>(
  function AddressAutocomplete(
    {
      value,
      onChange,
      sanitizeInput = sanitizeString,
      onSelect,
      onBlur: onBlurProp,
      placeholder = "Search address...",
      disabled = false,
      className,
      ...rest
    },
    ref
  ) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const anchorRef = useRef<HTMLDivElement>(null);
    const blurCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const listboxId = React.useId();
    const { results, isLoading, search, clear } = useAddressSearch();

    const cancelPendingBlurClose = useCallback(() => {
      if (blurCloseTimeoutRef.current === null) return;
      clearTimeout(blurCloseTimeoutRef.current);
      blurCloseTimeoutRef.current = null;
    }, []);

    React.useEffect(() => cancelPendingBlurClose, [cancelPendingBlurClose]);

    // Sync input value from prop when changed externally
    React.useEffect(() => {
      setInputValue(value);
    }, [value]);

    // Keep the highlighted option visible inside the scrollable listbox when
    // arrow keys move it past the fold ({block:"nearest"} is a no-op for
    // already-visible rows, so mouse-hover highlights don't jump the list)
    React.useEffect(() => {
      if (highlightedIndex < 0) return;
      document
        .getElementById(`${listboxId}-${highlightedIndex}`)
        ?.scrollIntoView({ block: "nearest" });
    }, [highlightedIndex, listboxId]);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const sanitized = sanitizeInput(e.target.value);
        setInputValue(sanitized);
        setHighlightedIndex(-1);
        onChange(sanitized);
        search(sanitized);
        if (sanitized.length >= 3) {
          setOpen(true);
        } else {
          setOpen(false);
        }
      },
      [onChange, search, sanitizeInput]
    );

    const handleSelect = useCallback(
      (result: AdsAddressResult) => {
        cancelPendingBlurClose();
        const address = sanitizeInput(formatAdsAddress(result));
        setInputValue(address);
        onChange(address);
        onSelect?.(result);
        setOpen(false);
        setHighlightedIndex(-1);
        clear();
      },
      [onChange, onSelect, clear, cancelPendingBlurClose, sanitizeInput]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
          cancelPendingBlurClose();
          setOpen(false);
          setHighlightedIndex(-1);
          clear();
          return;
        }
        if (results.length === 0) return;
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          if (!open) {
            setOpen(true);
            return;
          }
          setHighlightedIndex((i) =>
            e.key === "ArrowDown" ? (i + 1) % results.length : i <= 0 ? results.length - 1 : i - 1
          );
        } else if (e.key === "Enter" && open && results[highlightedIndex]) {
          e.preventDefault(); // Don't submit the surrounding form when picking a suggestion
          handleSelect(results[highlightedIndex]);
        }
      },
      [open, results, highlightedIndex, clear, handleSelect, cancelPendingBlurClose]
    );

    const handleBlur = useCallback(() => {
      // Small delay to allow click on dropdown items
      cancelPendingBlurClose();
      blurCloseTimeoutRef.current = setTimeout(() => {
        blurCloseTimeoutRef.current = null;
        setOpen(false);
        // Results survive a blur-close, so a stale highlight would let the
        // next Enter select a row the user never picked after refocus
        setHighlightedIndex(-1);
      }, 200);
      onBlurProp?.();
    }, [onBlurProp, cancelPendingBlurClose]);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div ref={anchorRef} className="relative w-full">
            <Input
              ref={ref}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                cancelPendingBlurClose();
                if (results.length > 0 && inputValue.length >= 3) {
                  setOpen(true);
                }
              }}
              onBlur={handleBlur}
              placeholder={placeholder}
              disabled={disabled}
              className={cn("rounded-xl pr-8", className)}
              autoComplete="off"
              role="combobox"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={
                highlightedIndex >= 0 ? `${listboxId}-${highlightedIndex}` : undefined
              }
              {...rest}
            />
            {isLoading && (
              <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </div>
        </PopoverAnchor>
        {results.length > 0 && (
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
            sideOffset={4}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <ul id={listboxId} role="listbox" className="max-h-[300px] overflow-y-auto">
              {results.map((result, index) => {
                const fullAddress = formatAdsAddress(result);
                // tunnus is the cadastral unit code — shared by every address
                // on the same plot, so it can't be the React key
                const key = result.ipikkaadress || result.taisaadress || index;
                return (
                  <li
                    key={key}
                    id={`${listboxId}-${index}`}
                    role="option"
                    aria-selected={index === highlightedIndex}
                    className={cn(
                      "flex cursor-pointer items-start gap-2 px-3 py-2 text-sm hover:bg-muted",
                      index === highlightedIndex && "bg-muted"
                    )}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent input blur before selection
                      handleSelect(result);
                    }}
                  >
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    {/* Long Estonian addresses differ only in their tail — wrap
                        instead of truncating so every input mode (mouse, keyboard,
                        touch) can tell rows apart. */}
                    <div className="flex min-w-0 flex-col">
                      <span className="break-words font-medium">{fullAddress}</span>
                      {result.tunnus && (
                        // break-all: cadastral codes like 78408:806:0013 have no
                        // natural break point and would overflow a narrow popover
                        <span className="break-all text-xs text-muted-foreground">
                          {result.tunnus}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </PopoverContent>
        )}
      </Popover>
    );
  }
);
